/**
 * Application Constants
 *
 * Central configuration for the application.
 */

/**
 * API Configuration
 */
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:5080/api/v1',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

/**
 * SSO Configuration
 *
 * The identity provider and portal used by every Roommatik app.
 * Values come from VITE_AUTH_* env vars (generic names on purpose).
 */
export const SSO_CONFIG = {
  URL: import.meta.env.VITE_AUTH_URL || 'https://auth.roommatik.com',
  REALM: import.meta.env.VITE_AUTH_REALM || 'roommatik',
  CLIENT_ID: import.meta.env.VITE_AUTH_CLIENT_ID || 'contacts',
  PORTAL_URL: import.meta.env.VITE_PORTAL_URL || 'https://portal.roommatik.com',
} as const;

/**
 * Authentication storage keys
 *
 * Generic names — they must not reveal the identity technology.
 */
export const AUTH_CONFIG = {
  TOKEN_KEY: 'token',
  ROLE_KEY: 'role',
  ROLES_KEY: 'roles',
  USER_KEY: 'user',
  AUTH_METHOD_KEY: 'auth_method',
} as const;

/**
 * Pagination Configuration
 */
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 1000,
} as const;

/**
 * User Roles
 *
 * Client roles assigned in the identity provider for this app.
 */
export const USER_ROLES = {
  SUPERUSER: 'superuser',
  COMERCIAL: 'comercial',
  MARKETING: 'marketing',
} as const;

/**
 * Status Colors for UI
 */
export const STATUS_COLORS = {
  active: '#4caf50',
  inactive: '#9e9e9e',
  pending: '#ff9800',
  error: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
} as const;

/**
 * Language Options
 */
export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

/**
 * Date Formats
 */
export const DATE_FORMATS = {
  DATE: 'YYYY-MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  DISPLAY_DATE: 'MMM D, YYYY',
  DISPLAY_DATETIME: 'MMM D, YYYY h:mm A',
} as const;

// ============================================
// DERIVED TYPES
// ============================================

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];
export type LanguageCode = (typeof LANGUAGE_OPTIONS)[number]['value'];

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getUserRoleValues = (): string[] => Object.values(USER_ROLES);

export const isValidUserRole = (role: string): role is UserRole => {
  return getUserRoleValues().includes(role);
};
